/**
 * MobileNetV2 embedding generator. Mirrors the runtime path used by
 * `majulah-hunt/src/lib/image-target-detector.ts` so the vectors produced
 * here are directly comparable to what the check-in flow sees.
 *
 *   blob (or static URL) → HTMLImageElement → model.infer(img, true)
 *
 * The MobileNet weights live alongside this app under
 * /models/mobilenet_v2_100_224/ and are byte-identical to the ones
 * majulah-hunt ships, so a cross-device divergence here is a divergence
 * in the runtime path itself, not in the weights.
 */

const MOBILENET_MODEL_URL = "/models/mobilenet_v2_100_224/model.json";
const EMBEDDING_DIM = 1280;

type MobileNetModel = {
  infer: (
    input: HTMLImageElement | HTMLCanvasElement | ImageBitmap,
    embedding?: boolean,
  ) => { dataSync(): Float32Array; dispose(): void };
};

/**
 * Which TF.js backend the run is using. `webgl` is the default and
 * matches the (current) check-in runtime. `wasm` mirrors the spike fix
 * for Mali GPUs (see PR #332 / tools/inference-check/samples/). `cpu`
 * is the pure-JS fallback — slow but a useful tiebreaker when wasm and
 * webgl disagree.
 */
export type Backend = "webgl" | "wasm" | "cpu";
export const BACKEND_VALUES: readonly Backend[] = ["webgl", "wasm", "cpu"];

let modelPromise: Promise<MobileNetModel> | null = null;
let backendName = "unknown";
let backendRequested: Backend = "webgl";

export function getBackendName(): string {
  return backendName;
}

export function getBackendRequested(): Backend {
  return backendRequested;
}

/**
 * Load the model on the requested TF.js backend.
 *
 * `requested` is recorded verbatim in the report so a reader can tell at
 * a glance "this run was forced to wasm via ?backend=wasm" vs. "this run
 * took the default webgl path." If the requested backend fails to
 * initialise (e.g. WebGL unavailable on a locked-down browser), TF.js
 * falls back internally and `getBackendName()` reports what actually ran.
 *
 * `requested` is optional so that downstream helpers (`embedImage`,
 * `embedVideoFrame`) can call `loadModel()` with no argument and get
 * whatever the App already initialised. When a caller DOES pass a value
 * and the cache already exists with a different backend, this throws —
 * the only supported way to switch backends in this tool is a full page
 * reload, so a silent mismatch would be a bug, not a feature.
 */
export async function loadModel(requested?: Backend): Promise<MobileNetModel> {
  if (modelPromise) {
    if (requested !== undefined && requested !== backendRequested) {
      throw new Error(
        `loadModel: cache was initialised with backend "${backendRequested}" ` +
          `but caller requested "${requested}". The model promise is cached ` +
          `for the page lifetime; reload the page with ?backend=${requested} ` +
          `to switch backends.`,
      );
    }
    return modelPromise;
  }
  const effective: Backend = requested ?? "webgl";
  backendRequested = effective;
  const p = (async () => {
    const tf = await import("@tensorflow/tfjs");
    if (effective === "wasm") {
      // Self-hosted WASM binaries — see tools/inference-check/public/tfjs-wasm/.
      // Same CSP-clean same-origin pattern as the MobileNet weights.
      const wasmMod = await import("@tensorflow/tfjs-backend-wasm");
      wasmMod.setWasmPaths("/tfjs-wasm/");
    }
    try {
      await tf.setBackend(effective);
      await tf.ready();
    } catch {
      // Fall through to whatever default backend tfjs registered.
    }
    backendName = tf.getBackend() ?? "unknown";
    const mod = await import("@tensorflow-models/mobilenet");
    return mod.load({
      version: 2,
      alpha: 1.0,
      modelUrl: MOBILENET_MODEL_URL,
      inputRange: [0, 1],
    }) as Promise<MobileNetModel>;
  })();
  p.catch(() => {
    if (modelPromise === p) modelPromise = null;
  });
  modelPromise = p;
  return modelPromise;
}

/** Decode any image source (URL or Blob) into an HTMLImageElement. */
export function loadImage(src: string | Blob): Promise<{ img: HTMLImageElement; url: string; owned: boolean }> {
  return new Promise((resolve, reject) => {
    const owned = src instanceof Blob;
    const url = owned ? URL.createObjectURL(src) : src;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve({ img, url, owned });
    img.onerror = () => {
      if (owned) URL.revokeObjectURL(url);
      reject(new Error(`Could not decode image: ${url}`));
    };
    img.src = url;
  });
}

/** Run a single image through MobileNet and return its 1280-dim embedding. */
export async function embedImage(src: string | Blob): Promise<Float32Array> {
  const model = await loadModel();
  const { img, url, owned } = await loadImage(src);
  try {
    const t = model.infer(img, true);
    try {
      const data = t.dataSync();
      if (data.length !== EMBEDDING_DIM) {
        throw new Error(`Unexpected embedding length ${data.length}, expected ${EMBEDDING_DIM}`);
      }
      // Copy out of the live buffer so we can dispose the tensor safely.
      return new Float32Array(data);
    } finally {
      t.dispose();
    }
  } finally {
    if (owned) URL.revokeObjectURL(url);
  }
}

/** Capture one frame from a live <video> via canvas+JPEG round-trip
 *  (the exact pipeline the check-in detector uses), then embed it. */
export async function embedVideoFrame(video: HTMLVideoElement): Promise<Float32Array> {
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth || 1280;
  canvas.height = video.videoHeight || 720;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context unavailable");
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
      "image/jpeg",
      0.9,
    );
  });
  return embedImage(blob);
}

export function cosineSimilarity(a: Float32Array | number[], b: Float32Array | number[]): number {
  // Length mismatch would silently iterate past the end of the shorter
  // array, producing NaN that then propagates into the JSON report. Bad
  // reference payloads (e.g. a corrupted referenceEmbeddingsUrl) should
  // fail loudly here rather than confuse downstream analysis.
  if (a.length !== b.length) {
    throw new Error(
      `cosineSimilarity: vector length mismatch (${a.length} vs ${b.length})`,
    );
  }
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  if (denom === 0) return 0;
  return dot / denom;
}

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Compact summary of a vector: full vector, L2 norm, first 6 components
 *  (the same "fingerprint" the runtime debug overlay shows), and stats. */
export interface EmbeddingSummary {
  vector: number[];
  l2Norm: number;
  fingerprint: string;
  min: number;
  max: number;
  mean: number;
}

export function summariseEmbedding(vec: Float32Array): EmbeddingSummary {
  let sumSq = 0;
  let sum = 0;
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < vec.length; i++) {
    const v = vec[i];
    sumSq += v * v;
    sum += v;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  const fpLen = Math.min(6, vec.length);
  let fp = "";
  for (let i = 0; i < fpLen; i++) {
    fp += Math.abs(vec[i] * 1000).toFixed(0).padStart(4, "0") + " ";
  }
  return {
    // JSON-friendly: float values truncated to 6 decimals to keep
    // payloads small while preserving comparability to within ~1e-6.
    vector: Array.from(vec, (v) => Number(v.toFixed(6))),
    l2Norm: Math.sqrt(sumSq),
    fingerprint: fp.trim(),
    min,
    max,
    mean: sum / vec.length,
  };
}
